import { bbox as turfBbox } from '@turf/turf'
import type { Feature, Polygon } from 'geojson'
import maplibregl from 'maplibre-gl'
import { CommuneLayerService } from '@/services/map/CommuneLayerService'

export interface CommuneViewData {
  bounds: maplibregl.LngLatBounds
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
}

const COMMUNE_SOURCE_ID = CommuneLayerService.getSourceId()
const SOURCE_LAYER = 'commune'

const MAX_RETRIES = 6
const RETRY_DELAY_MS = 300

function normalizeCommuneName(name: string): string {
  return name.trim().toLowerCase()
}

function featureNameMatches(feature: maplibregl.MapGeoJSONFeature, communeName: string): boolean {
  const normalized = normalizeCommuneName(communeName)
  const props = feature.properties
  if (!props) return false
  const candidates = [
    props.cmn_titre as string | undefined,
    props.nom as string | undefined,
    props.name as string | undefined,
  ].filter(Boolean) as string[]
  return candidates.some((c) => normalizeCommuneName(c) === normalized)
}

const SOURCE_DATA_TIMEOUT_MS = 2000

/**
 * Resolves when the commune source fires a sourcedata event, or after SOURCE_DATA_TIMEOUT_MS.
 * Prevents infinite hang when the commune layer is not loaded (e.g. visibility: none).
 */
function whenCommuneSourceData(map: maplibregl.Map): Promise<void> {
  return new Promise((resolve) => {
    const handler = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId === COMMUNE_SOURCE_ID) {
        map.off('sourcedata', handler)
        clearTimeout(timer)
        resolve()
      }
    }
    const timer = setTimeout(() => {
      map.off('sourcedata', handler)
      resolve()
    }, SOURCE_DATA_TIMEOUT_MS)
    map.on('sourcedata', handler)
  })
}

/**
 * Finds the commune feature by name from the commune vector layer and returns its bounds and geometry.
 * Waits for the commune source to load and retries until a matching feature is found or timeout.
 */
export function getCommuneBoundsFromMap(
  map: maplibregl.Map,
  communeName: string,
): Promise<CommuneViewData | null> {
  if (!communeName.trim()) {
    return Promise.resolve(null)
  }

  const source = map.getSource(COMMUNE_SOURCE_ID)
  if (!source) {
    return Promise.resolve(null)
  }

  function tryQuery(): maplibregl.MapGeoJSONFeature | null {
    const features = map.querySourceFeatures(COMMUNE_SOURCE_ID, {
      sourceLayer: SOURCE_LAYER,
    })
    const match = features.find((f) => featureNameMatches(f, communeName))
    return match ?? null
  }

  function featureToCommuneViewData(feature: maplibregl.MapGeoJSONFeature): CommuneViewData {
    const geoJsonFeature: Feature = {
      type: 'Feature',
      properties: feature.properties ?? {},
      geometry: feature.geometry,
    }
    const [minLng, minLat, maxLng, maxLat] = turfBbox(geoJsonFeature)
    const bounds = new maplibregl.LngLatBounds([minLng, minLat], [maxLng, maxLat])
    const geometry = feature.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon
    return { bounds, geometry }
  }

  let attempt = 0
  function attemptQuery(): Promise<CommuneViewData | null> {
    const feature = tryQuery()
    if (feature) {
      return Promise.resolve(featureToCommuneViewData(feature))
    }
    attempt++
    if (attempt >= MAX_RETRIES) {
      return Promise.resolve(null)
    }
    return whenCommuneSourceData(map).then(
      () =>
        new Promise<CommuneViewData | null>((resolve) => {
          setTimeout(() => attemptQuery().then(resolve), RETRY_DELAY_MS)
        }),
    )
  }
  return attemptQuery()
}

const WORLD_RING: [number, number][] = [
  [-180, 90],
  [180, 90],
  [180, -90],
  [-180, -90],
  [-180, 90],
]

/**
 * Collects exterior rings from a Polygon or MultiPolygon (one ring per polygon part).
 */
function getExteriorRings(geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): number[][][] {
  if (geometry.type === 'Polygon') {
    return [geometry.coordinates[0]]
  }
  return geometry.coordinates.map((poly) => poly[0])
}

/**
 * Builds a GeoJSON FeatureCollection for the "world minus commune" mask:
 * one polygon with world as exterior and the commune geometry as hole(s), so only the commune is visible and the rest (other communes + world) is black, like the Cameroon mask.
 */
export function buildCommuneMaskGeoJSONFromGeometry(
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): GeoJSON.FeatureCollection {
  const holeRings = getExteriorRings(geometry) as [number, number][][]
  const polygon: Polygon = {
    type: 'Polygon',
    coordinates: [WORLD_RING, ...holeRings],
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: polygon,
      },
    ]
  }
}

/**
 * Builds a GeoJSON FeatureCollection for the "world minus bbox" mask (fallback when geometry is not used).
 * bbox: [minLng, minLat, maxLng, maxLat]
 */
export function buildCommuneMaskGeoJSON(
  bbox: [number, number, number, number],
): GeoJSON.FeatureCollection {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const holeRing: [number, number][] = [
    [minLng, minLat],
    [maxLng, minLat],
    [maxLng, maxLat],
    [minLng, maxLat],
    [minLng, minLat],
  ]
  const polygon: Polygon = {
    type: 'Polygon',
    coordinates: [WORLD_RING, holeRing],
  }
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: polygon,
      },
    ]
  }
}
